/**
 * The VFX particle pool (story `q-demo-vfx-pass`). A fixed-capacity structure-of-arrays:
 * every particle lives in preallocated typed arrays, a spawn past capacity is DROPPED
 * (never grows), and a dead particle is swap-removed so the live set stays packed at the
 * front. Nothing here allocates after construction — the per-frame path (`update`) is
 * arithmetic over the arrays. Pure: no `three`, no DOM, a seeded RNG for tests.
 */

import type { BurstPreset, VfxLayer } from './presets.js';
import { VFX_LAYERS } from './presets.js';

/** A small deterministic RNG (mulberry32) — tests pin bursts; the game seeds it from time. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const LAYER_INDEX: Readonly<Record<VfxLayer, number>> = Object.fromEntries(
  VFX_LAYERS.map((l, i) => [l, i]),
) as Record<VfxLayer, number>;

export class ParticlePool {
  readonly capacity: number;
  /** Live particles occupy [0, count). */
  count = 0;
  /** Spawns refused because the pool was full (since construction). */
  dropped = 0;
  /** The most particles live at once (since construction / `resetPeak`). */
  peak = 0;

  readonly px: Float32Array;
  readonly py: Float32Array;
  readonly pz: Float32Array;
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  readonly vz: Float32Array;
  readonly age: Float32Array;
  readonly life: Float32Array;
  readonly size0: Float32Array;
  readonly size1: Float32Array;
  /** Colour at birth and at death (linear 0..1), lerped by age. */
  readonly r0: Float32Array;
  readonly g0: Float32Array;
  readonly b0: Float32Array;
  readonly r1: Float32Array;
  readonly g1: Float32Array;
  readonly b1: Float32Array;
  readonly gravity: Float32Array;
  readonly drag: Float32Array;
  readonly layer: Uint8Array;
  /** Live particles per layer, recomputed by `update` (indexed as `VFX_LAYERS`). */
  readonly perLayer: Uint16Array;

  private readonly rand: () => number;
  /** A hold factor in [0, 1]: ages advance by dt × (1 − hold); 1 freezes. DEV lane only. */
  hold = 0;

  constructor(capacity: number, rand: () => number = Math.random) {
    this.capacity = Math.max(1, Math.floor(capacity));
    const n = this.capacity;
    this.px = new Float32Array(n);
    this.py = new Float32Array(n);
    this.pz = new Float32Array(n);
    this.vx = new Float32Array(n);
    this.vy = new Float32Array(n);
    this.vz = new Float32Array(n);
    this.age = new Float32Array(n);
    this.life = new Float32Array(n);
    this.size0 = new Float32Array(n);
    this.size1 = new Float32Array(n);
    this.r0 = new Float32Array(n);
    this.g0 = new Float32Array(n);
    this.b0 = new Float32Array(n);
    this.r1 = new Float32Array(n);
    this.g1 = new Float32Array(n);
    this.b1 = new Float32Array(n);
    this.gravity = new Float32Array(n);
    this.drag = new Float32Array(n);
    this.layer = new Uint8Array(n);
    this.perLayer = new Uint16Array(VFX_LAYERS.length);
    this.rand = rand;
  }

  /**
   * Emit `preset` at (x, y, z) scaled by `scale` (radius-sized effects) and `countScale`
   * (the reduced-effects setting). Returns how many particles were actually spawned.
   */
  burst(preset: BurstPreset, x: number, y: number, z: number, scale = 1, countScale = 1): number {
    const want = Math.max(1, Math.round(preset.count * countScale));
    const rand = this.rand;
    const li = LAYER_INDEX[preset.layer];
    const c0 = preset.color;
    const c1 = preset.color2 ?? preset.color;
    let made = 0;
    for (let k = 0; k < want; k += 1) {
      if (this.count >= this.capacity) {
        this.dropped += want - k;
        break;
      }
      const i = this.count;
      this.count += 1;
      made += 1;
      // Direction by spread shape.
      let dx: number;
      let dy: number;
      let dz: number;
      const theta = rand() * Math.PI * 2;
      switch (preset.spread) {
        case 'column':
        case 'up': {
          const tilt = rand() * 0.45;
          dx = Math.cos(theta) * tilt;
          dz = Math.sin(theta) * tilt;
          dy = 1;
          break;
        }
        case 'flat':
          dx = Math.cos(theta);
          dz = Math.sin(theta);
          dy = 0.15 + rand() * 0.25;
          break;
        case 'still':
          dx = 0;
          dy = 0;
          dz = 0;
          break;
        default: {
          // sphere: uniform-ish direction
          const u = rand() * 2 - 1;
          const s = Math.sqrt(1 - u * u);
          dx = Math.cos(theta) * s;
          dz = Math.sin(theta) * s;
          dy = u;
        }
      }
      const speed = (preset.speed[0] + rand() * (preset.speed[1] - preset.speed[0])) * scale;
      const jitter = preset.jitter * scale;
      if (preset.spread === 'column') {
        // on a circle of radius  round the anchor: a column of light AROUND a body
        this.px[i] = x + Math.cos(theta) * jitter;
        this.py[i] = y + preset.height + rand() * 0.3;
        this.pz[i] = z + Math.sin(theta) * jitter;
      } else {
        this.px[i] = x + (rand() * 2 - 1) * jitter;
        this.py[i] = y + preset.height + (rand() * 2 - 1) * jitter * 0.5;
        this.pz[i] = z + (rand() * 2 - 1) * jitter;
      }
      this.vx[i] = dx * speed;
      this.vy[i] = dy * speed;
      this.vz[i] = dz * speed;
      this.age[i] = 0;
      this.life[i] = preset.life[0] + rand() * (preset.life[1] - preset.life[0]);
      this.size0[i] = preset.size[0] * scale;
      this.size1[i] = preset.size[1] * scale;
      this.r0[i] = c0[0];
      this.g0[i] = c0[1];
      this.b0[i] = c0[2];
      this.r1[i] = c1[0];
      this.g1[i] = c1[1];
      this.b1[i] = c1[2];
      this.gravity[i] = preset.gravity;
      this.drag[i] = preset.drag;
      this.layer[i] = li;
    }
    if (this.count > this.peak) this.peak = this.count;
    return made;
  }

  /** Advance every live particle by `dt` seconds; expire and compact. Allocation-free. */
  update(dt: number): void {
    const step = dt * (1 - this.hold);
    this.perLayer.fill(0);
    let i = 0;
    while (i < this.count) {
      const a = this.age[i]! + step;
      if (a >= this.life[i]!) {
        this.kill(i);
        continue; // the swapped-in particle now sits at i
      }
      this.age[i] = a;
      const damp = Math.max(0, 1 - this.drag[i]! * step);
      const vx = this.vx[i]! * damp;
      const vz = this.vz[i]! * damp;
      const vy = this.vy[i]! * damp - this.gravity[i]! * step;
      this.vx[i] = vx;
      this.vy[i] = vy;
      this.vz[i] = vz;
      this.px[i] = this.px[i]! + vx * step;
      this.py[i] = this.py[i]! + vy * step;
      this.pz[i] = this.pz[i]! + vz * step;
      const li = this.layer[i]!;
      this.perLayer[li] = this.perLayer[li]! + 1;
      i += 1;
    }
  }

  /** Normalised age in [0, 1]. */
  t(i: number): number {
    return this.age[i]! / this.life[i]!;
  }

  clear(): void {
    this.count = 0;
    this.perLayer.fill(0);
  }

  resetPeak(): void {
    this.peak = this.count;
  }

  private kill(i: number): void {
    const last = this.count - 1;
    if (i !== last) {
      this.px[i] = this.px[last]!;
      this.py[i] = this.py[last]!;
      this.pz[i] = this.pz[last]!;
      this.vx[i] = this.vx[last]!;
      this.vy[i] = this.vy[last]!;
      this.vz[i] = this.vz[last]!;
      this.age[i] = this.age[last]!;
      this.life[i] = this.life[last]!;
      this.size0[i] = this.size0[last]!;
      this.size1[i] = this.size1[last]!;
      this.r0[i] = this.r0[last]!;
      this.g0[i] = this.g0[last]!;
      this.b0[i] = this.b0[last]!;
      this.r1[i] = this.r1[last]!;
      this.g1[i] = this.g1[last]!;
      this.b1[i] = this.b1[last]!;
      this.gravity[i] = this.gravity[last]!;
      this.drag[i] = this.drag[last]!;
      this.layer[i] = this.layer[last]!;
    }
    this.count = last;
  }
}
